import { AutoTagStatusResponse, AutoTagTableStatus } from "@/apis/table/route";
import queries from "@/queries";
import { useQuery } from "@tanstack/react-query";

const AUTO_TAG_POLL_INTERVAL_MS = 2000;

/** 태스크가 한 번이라도 생성된 표인지. status null 은 미실행 표다. */
export const hasAutoTagRecord = (table: AutoTagTableStatus) =>
  table.status !== null;

export const isAutoTagRunning = (table: AutoTagTableStatus) =>
  table.status === "PENDING" || table.status === "RUNNING";

// status 응답은 미실행 표(status null)도 포함하므로 "전부 terminal"이 아니라
// "진행 중 행 없음"이 종료 조건이다. 비어 있으면 some([]) === false → 폴링 중지.
export const checkAutoTagFinished = (data: AutoTagStatusResponse) =>
  !data.tables.some(isAutoTagRunning);

/**
 * 워크북 오토태깅 진행 상태.
 *
 * 기본은 읽기 전용 구독 — 표시 컴포넌트들은 같은 queryKey 캐시만 본다. 폴링은
 * 매핑 페이지 루트에 항상 떠 있는 `useAutoTagCompletionEffect` 한 곳만 켠다.
 * (observer 마다 refetchInterval 타이머가 따로 돌아, 폴링 옵저버 수만큼 같은
 * GET 이 중복 발사되기 때문. 단일 폴러 덕에 오토태깅 섹션을 안 열어도 갱신된다.)
 * 폴링 중지는 데이터 기반 — 진행 중(PENDING/RUNNING) 표가 없으면 멈춘다.
 */
export const useAutoTagStatus = (
  workbookId: string | undefined,
  options?: { enablePolling?: boolean },
) => {
  const enablePolling = options?.enablePolling ?? false;
  return useQuery({
    ...queries.workbook(workbookId ?? "")._ctx.autoTagStatus(),
    enabled: !!workbookId,
    refetchInterval: (query) => {
      if (!enablePolling) return false;
      const data = query.state.data as AutoTagStatusResponse | undefined;
      if (data && checkAutoTagFinished(data)) return false;
      return AUTO_TAG_POLL_INTERVAL_MS;
    },
    gcTime: 0,
    staleTime: 0,
  });
};
